import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, TextInput, Platform, Image 
} from 'react-native';
import { Button, Icon } from 'react-native-elements';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import * as Speech from 'expo-speech';
import { soundService } from '../services/soundService';
import wordsData from '../data/words.json';
import { testWordComparison } from '../utils/wordComparison';
import { colors, spacing, typography, globalStyles } from '../styles/globalStyles';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { ParamListBase } from '@react-navigation/native';

declare global {
  interface Window {
    webkitSpeechRecognition: any;
  }
}

type PlayerResults = {
  time: number;
  missedWords: string[];
  attempts: number;
  sessionResults: Array<{
    word: string;
    attempts: number;
    success: boolean;
    transcript: string;
  }>;
};

export default function GameModeScreen() {
  const { user } = useAuth();
  const navigation = useNavigation<BottomTabNavigationProp<ParamListBase>>();

  // Game settings state
  const [wordsPerTurn, setWordsPerTurn] = useState(5);
  const [attemptsAllowed, setAttemptsAllowed] = useState(3);
  const [gameStarted, setGameStarted] = useState(false);
  const [player1Turn, setPlayer1Turn] = useState(true);
  const [player1Name, setPlayer1Name] = useState('Player 1');
  const [player2Name, setPlayer2Name] = useState('Player 2');
  const [player1Grade, setPlayer1Grade] = useState("grade1");
  const [player2Grade, setPlayer2Grade] = useState("grade1");
  const [player1Difficulty, setPlayer1Difficulty] = useState("beginner");
  const [player2Difficulty, setPlayer2Difficulty] = useState("beginner");
  const [player1Results, setPlayer1Results] = useState<PlayerResults>();
  const [player2Results, setPlayer2Results] = useState<PlayerResults>();
  const [gameStartTime, setGameStartTime] = useState<number>(0);
  const [turnStartTime, setTurnStartTime] = useState<number>(0);
  const [isGameComplete, setIsGameComplete] = useState(false);

  // Gameplay state
  const [currentWord, setCurrentWord] = useState('');
  const currentWordRef = useRef(currentWord);
  const [isListening, setIsListening] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const isProcessingRef = useRef(false);
  const recognitionRef = useRef<any>(null);
  const [recognizedText, setRecognizedText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [wordsRemaining, setWordsRemaining] = useState(wordsPerTurn);
  const wordsRemainingRef = useRef(wordsPerTurn);
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);
  const [wordCompleted, setWordCompleted] = useState(false);
  const [sessionResults, setSessionResults] = useState<Array<{
    word: string;
    attempts: number;
    success: boolean;
    transcript: string;
  }>>([]);
  const sessionResultsRef = useRef(sessionResults);
  const [missedWords, setMissedWords] = useState<string[]>([]);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [turnReady, setTurnReady] = useState(true);
  const [shouldRestartRecognition, setShouldRestartRecognition] = useState(true);

  useEffect(() => {
    sessionResultsRef.current = sessionResults;
  }, [sessionResults]);

  useEffect(() => {
    currentWordRef.current = currentWord;
  }, [currentWord]);

  useEffect(() => {
    if (!gameStarted) {
      setWordsRemaining(wordsPerTurn);
      wordsRemainingRef.current = wordsPerTurn;
      console.log(`Initializing words remaining to ${wordsPerTurn}`);
    }
  }, [wordsPerTurn, gameStarted]);

  const playSound = useCallback((type: 'correct' | 'incorrect') => {
    soundService.playSound(type);
  }, []);

  const calculatePlayerScore = (results: PlayerResults) => {
    if (!results) return 0;
    const timeScore = Math.max(0, 1000 - Math.floor(results.time / 100)); // Time bonus
    const accuracyScore = Math.floor((results.sessionResults.filter(r => r.success).length / results.sessionResults.length) * 1000);
    const attemptsScore = Math.max(0, 500 - (results.attempts * 50)); // Fewer attempts = higher score
    return timeScore + accuracyScore + attemptsScore;
  };

  const determineWinner = useCallback(() => {
    if (!player1Results || !player2Results) return null;
    
    const p1Score = calculatePlayerScore(player1Results);
    const p2Score = calculatePlayerScore(player2Results);
    
    return {
      winner: p1Score > p2Score ? 1 : 2,
      player1Score: p1Score,
      player2Score: p2Score,
      details: {
        timeComparison: player1Results.time - player2Results.time,
        accuracyComparison: 
          (player1Results.missedWords.length - player2Results.missedWords.length),
        attemptsComparison: 
          (player1Results.attempts - player2Results.attempts)
      }
    };
  }, [player1Results, player2Results]);

  const saveGameResults = async (winner: number) => {
    try {
      const { data, error } = await supabase
        .from('game_sessions')
        .insert({
          user_id: user?.id,
          player1_grade: player1Grade,
          player2_grade: player2Grade,
          player1_difficulty: player1Difficulty,
          player2_difficulty: player2Difficulty,
          player1_time: player1Results?.time,
          player1_missed_words: player1Results?.missedWords,
          player1_attempts: player1Results?.attempts,
          player2_time: player2Results?.time,
          player2_missed_words: player2Results?.missedWords,
          player2_attempts: player2Results?.attempts,
          winner,
          date: new Date().toISOString(),
          session_type: 'competitive'
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving game results:', error);
      Alert.alert('Error', 'Failed to save game results');
    }
  };

  const handleTurnComplete = useCallback(() => {
    console.log('Handling turn completion');
    const endTime = Date.now();
    const turnResults = {
      time: endTime - turnStartTime,
      missedWords,
      attempts: attemptsRef.current,
      sessionResults: sessionResultsRef.current
    };
    
    if (player1Turn) {
      console.log(`Player 1 turn complete, preparing for Player 2's turn`);
      setPlayer1Results(turnResults);
      setPlayer1Turn(false);
      
      // Stop current turn activities
      setShouldRestartRecognition(false); // Prevent recognition restart
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      
      // Reset for player 2's turn
      setWordsRemaining(wordsPerTurn);
      wordsRemainingRef.current = wordsPerTurn;
      setSessionResults([]);
      setMissedWords([]);
      attemptsRef.current = 0;
      setAttempts(0);
      setScore(0);
      setStreak(0);
      setCurrentWord('');
      setWordCompleted(false);
      setTurnReady(false);
      setShowTransitionScreen(true);
    } else {
      console.log('Player 2 turn complete, game over');
      setPlayer2Results(turnResults);
      setIsGameComplete(true);
      setShouldRestartRecognition(false);
      const winner = determineWinner();
      if (winner) {
        saveGameResults(winner.winner);
      }
    }
  }, [player1Turn, turnStartTime, missedWords, determineWinner, wordsPerTurn]);

  const processResult = useCallback((transcript: string) => {
    if (isProcessingRef.current || wordCompleted) return;
    isProcessingRef.current = true;
    
    try {
      const isCorrect = testWordComparison(transcript, currentWordRef.current);
      
      const newAttempts = attemptsRef.current + 1;
      setAttempts(newAttempts);
      attemptsRef.current = newAttempts;
      
      if (isCorrect || newAttempts >= attemptsAllowed) {
        const result = {
          word: currentWordRef.current,
          attempts: newAttempts,
          success: isCorrect,
          transcript
        };
        setSessionResults(prev => [...prev, result]);
        setWordCompleted(true);

        if (isCorrect) {
          playSound('correct');
          setScore(prev => prev + 10);
          setStreak(prev => prev + 1);
        } else {
          playSound('incorrect');
          setStreak(0);
          setMissedWords(prev => [...prev, currentWordRef.current]);
        }

        // Update words remaining using ref
        const newWordsRemaining = wordsRemainingRef.current - 1;
        wordsRemainingRef.current = newWordsRemaining;
        setWordsRemaining(newWordsRemaining);
        console.log(`Words remaining: ${newWordsRemaining} (Total words per turn: ${wordsPerTurn})`);
        
        if (newWordsRemaining <= 0) {
          console.log('Turn complete, handling turn transition');
          setTimeout(() => {
            handleTurnComplete();
          }, 1000);
        } else {
          console.log('Moving to next word');
          setTimeout(() => {
            nextWord();
          }, 1000);
        }
      } else {
        playSound('incorrect');
        setStreak(0);
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [attemptsAllowed, wordsPerTurn, handleTurnComplete, playSound, wordCompleted]);

  const nextWord = useCallback(() => {
    const currentGrade = player1Turn ? player1Grade : player2Grade;
    const currentDifficulty = player1Turn ? player1Difficulty : player2Difficulty;
    const availableWords = wordsData[currentGrade][currentDifficulty];
    
    if (availableWords.length === 0) {
      Alert.alert('No Words Available', 'Please select a different grade or difficulty level.');
      return;
    }
    
    const randomIndex = Math.floor(Math.random() * availableWords.length);
    const newWord = availableWords[randomIndex];
    setCurrentWord(newWord);
    currentWordRef.current = newWord;
    setWordCompleted(false);
    setAttempts(0);
    attemptsRef.current = 0;
    setRecognizedText('');
    setInterimText('');
  }, [player1Turn, player1Grade, player2Grade, player1Difficulty, player2Difficulty]);

  // Speech Recognition Setup
  const setupRecognition = useCallback(() => {
    if (Platform.OS === 'web' && window.webkitSpeechRecognition) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        setInterimText('Listening...');
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[event.results.length - 1][0].transcript;
        if (event.results[event.results.length - 1].isFinal) {
          setRecognizedText(transcript);
          processResult(transcript);
        } else {
          setInterimText(transcript);
        }
      };

      recognition.onerror = (event: any) => {
        console.log('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        setIsListening(false);
        if (!isGameComplete && shouldRestartRecognition) {
          recognition.start();
        }
      };

      return recognition;
    }
    return null;
  }, [processResult, isGameComplete, shouldRestartRecognition]);

  const startListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    recognitionRef.current = setupRecognition();
    
    if (recognitionRef.current) {
      recognitionRef.current.start();
    } else {
      Alert.alert('Not Supported', 'Speech recognition is not supported on this platform.');
    }
  }, [setupRecognition]);

  const handleStartGame = useCallback(() => {
    console.log(`Starting game with ${wordsPerTurn} words per turn`);
    setGameStarted(true);
    setGameStartTime(Date.now());
    setTurnStartTime(Date.now());
    setWordsRemaining(wordsPerTurn);
    wordsRemainingRef.current = wordsPerTurn;
    setShouldRestartRecognition(true); // Enable recognition restart
    nextWord();
    startListening();
  }, [nextWord, startListening, wordsPerTurn]);

  const speakWord = useCallback(() => {
    Speech.speak(currentWord, {
      language: 'en',
      pitch: 1,
      rate: 0.75,
    });
  }, [currentWord]);

  const handleStartTurn = useCallback(() => {
    setShowTransitionScreen(false);
    setTurnReady(true);
    setTurnStartTime(Date.now());
    setShouldRestartRecognition(true); // Enable recognition restart
    nextWord();
    startListening();
  }, [nextWord, startListening]);

  const renderGameSetup = () => (
    <ScrollView contentContainerStyle={[globalStyles.contentContainer]}>
      <Text style={[typography.title, styles.setupTitle]}>Say It & Slay It</Text>
      
      <View style={[globalStyles.card, styles.settingsCard]}>
        <Text style={typography.subtitle}>Game Settings</Text>
        <View style={styles.settingRow}>
          <Text style={typography.body}>Words per Turn:</Text>
          <View style={styles.counterContainer}>
            <TouchableOpacity 
              onPress={() => {
                const newValue = Math.max(3, wordsPerTurn - 1);
                console.log(`Setting words per turn to ${newValue}`);
                setWordsPerTurn(newValue);
              }}
              style={styles.counterButton}
            >
              <Text style={styles.counterButtonText}>-</Text>
            </TouchableOpacity>
            <Text style={styles.counterValue}>{wordsPerTurn}</Text>
            <TouchableOpacity 
              onPress={() => {
                const newValue = Math.min(10, wordsPerTurn + 1);
                console.log(`Setting words per turn to ${newValue}`);
                setWordsPerTurn(newValue);
              }}
              style={styles.counterButton}
            >
              <Text style={styles.counterButtonText}>+</Text>
            </TouchableOpacity>
          </View>
          <Text style={[typography.caption, styles.settingHint]}>
            Each player will get {wordsPerTurn} words
          </Text>
        </View>

        <View style={styles.settingRow}>
          <Text style={typography.body}>Attempts Allowed:</Text>
          <View style={styles.counterContainer}>
            <TouchableOpacity 
              onPress={() => setAttemptsAllowed(prev => Math.max(1, prev - 1))}
              style={styles.counterButton}
            >
              <Text style={styles.counterButtonText}>-</Text>
            </TouchableOpacity>
            <Text style={styles.counterValue}>{attemptsAllowed}</Text>
            <TouchableOpacity 
              onPress={() => setAttemptsAllowed(prev => Math.min(5, prev + 1))}
              style={styles.counterButton}
            >
              <Text style={styles.counterButtonText}>+</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <View style={[globalStyles.card, styles.playerSetup]}>
        <Text style={typography.subtitle}>Player 1</Text>
        <TextInput
          style={globalStyles.input}
          value={player1Name}
          onChangeText={setPlayer1Name}
          placeholder="Enter name"
        />
        <Text style={typography.body}>Grade Level:</Text>
        <View style={styles.gradeSelector}>
          {Object.keys(wordsData).map((grade) => (
            <Button
              key={grade}
              title={grade.replace('grade', 'Grade ')}
              onPress={() => setPlayer1Grade(grade)}
              buttonStyle={[
                styles.gradeButton,
                player1Grade === grade && styles.selectedGrade
              ]}
            />
          ))}
        </View>
      </View>

      <View style={[globalStyles.card, styles.playerSetup]}>
        <Text style={typography.subtitle}>Player 2</Text>
        <TextInput
          style={globalStyles.input}
          value={player2Name}
          onChangeText={setPlayer2Name}
          placeholder="Enter name"
        />
        <Text style={typography.body}>Grade Level:</Text>
        <View style={styles.gradeSelector}>
          {Object.keys(wordsData).map((grade) => (
            <Button
              key={grade}
              title={grade.replace('grade', 'Grade ')}
              onPress={() => setPlayer2Grade(grade)}
              buttonStyle={[
                styles.gradeButton,
                player2Grade === grade && styles.selectedGrade
              ]}
            />
          ))}
        </View>
      </View>

      <Button
        title="Start Game"
        onPress={handleStartGame}
        buttonStyle={[globalStyles.buttonPrimary, styles.startButton]}
        titleStyle={globalStyles.buttonTextPrimary}
      />
    </ScrollView>
  );

  const renderGameplay = () => {
    if (showTransitionScreen) {
      return (
        <View style={[globalStyles.container, styles.transitionContainer]}>
          <View style={[globalStyles.card, styles.transitionCard]}>
            <Text style={typography.title}>{player2Name}'s Turn</Text>
            <Text style={[typography.body, styles.transitionText]}>
              Get ready! You'll have {wordsPerTurn} words to complete.
            </Text>
            <Button
              title="Start My Turn"
              onPress={handleStartTurn}
              buttonStyle={[globalStyles.buttonPrimary, styles.startTurnButton]}
              titleStyle={globalStyles.buttonTextPrimary}
            />
          </View>
        </View>
      );
    }

    return (
      <View style={[globalStyles.container, styles.gameplayContainer]}>
        <View style={[globalStyles.card, styles.headerContainer]}>
          <Text style={typography.title}>
            {player1Turn ? player1Name : player2Name}'s Turn
          </Text>
          <View style={globalStyles.statsContainer}>
            <View style={globalStyles.statBox}>
              <Text style={globalStyles.statLabel}>Words Left</Text>
              <Text style={globalStyles.statValue}>{wordsRemaining}</Text>
            </View>
            <View style={globalStyles.statBox}>
              <Text style={globalStyles.statLabel}>Attempts</Text>
              <Text style={globalStyles.statValue}>{attempts}/{attemptsAllowed}</Text>
            </View>
            <View style={globalStyles.statBox}>
              <Text style={globalStyles.statLabel}>Score</Text>
              <Text style={globalStyles.statValue}>{score}</Text>
            </View>
          </View>
        </View>

        <View style={styles.wordContainer}>
          {turnReady && (
            <>
              <Text style={globalStyles.wordDisplay}>{currentWord}</Text>
              <TouchableOpacity onPress={speakWord} style={styles.speakerButton}>
                <Icon name="volume-up" type="font-awesome" size={24} color={colors.primary} />
              </TouchableOpacity>
            </>
          )}
        </View>

        <View style={styles.recognitionContainer}>
          <Text style={typography.body}>
            {isListening ? "Listening..." : ""}
          </Text>
          <Text style={[typography.body, styles.recognizedText]}>
            {recognizedText ? `Heard: ${recognizedText}` : ''}
          </Text>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    const winner = determineWinner();
    if (!winner || !player1Results || !player2Results) return null;

    return (
      <ScrollView contentContainerStyle={[globalStyles.contentContainer]}>
        <Text style={typography.title}>Game Results</Text>
        <Text style={[typography.subtitle, styles.winnerText]}>
          {winner.winner === 1 ? player1Name : player2Name} Wins!
        </Text>

        <View style={[globalStyles.card, styles.scoreContainer]}>
          <View style={styles.playerScore}>
            <Text style={typography.subtitle}>{player1Name}</Text>
            <Text style={globalStyles.scoreText}>Score: {winner.player1Score}</Text>
            <Text style={typography.body}>
              Time: {(player1Results.time / 1000).toFixed(1)}s
            </Text>
            <Text style={typography.body}>
              Accuracy: {Math.round((player1Results.sessionResults.filter(r => r.success).length / player1Results.sessionResults.length) * 100)}%
            </Text>
          </View>

          <View style={styles.playerScore}>
            <Text style={typography.subtitle}>{player2Name}</Text>
            <Text style={globalStyles.scoreText}>Score: {winner.player2Score}</Text>
            <Text style={typography.body}>
              Time: {(player2Results.time / 1000).toFixed(1)}s
            </Text>
            <Text style={typography.body}>
              Accuracy: {Math.round((player2Results.sessionResults.filter(r => r.success).length / player2Results.sessionResults.length) * 100)}%
            </Text>
          </View>
        </View>

        <Button
          title="Play Again"
          onPress={() => {
            setGameStarted(false);
            setIsGameComplete(false);
            setPlayer1Results(undefined);
            setPlayer2Results(undefined);
            setPlayer1Turn(true);
          }}
          buttonStyle={[globalStyles.buttonPrimary, styles.playAgainButton]}
          titleStyle={globalStyles.buttonTextPrimary}
        />
      </ScrollView>
    );
  };

  return (
    <View style={globalStyles.container}>
      {!gameStarted && !isGameComplete && renderGameSetup()}
      {gameStarted && !isGameComplete && renderGameplay()}
      {isGameComplete && renderResults()}
    </View>
  );
}

const styles = StyleSheet.create({
  setupTitle: {
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  settingsCard: {
    marginBottom: spacing.lg,
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: spacing.sm,
  },
  counterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  counterButton: {
    backgroundColor: colors.primary,
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  counterButtonText: {
    color: colors.surface,
    fontSize: 20,
    fontWeight: 'bold',
  },
  counterValue: {
    fontSize: 20,
    marginHorizontal: spacing.md,
  },
  playerSetup: {
    marginBottom: spacing.lg,
  },
  gradeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  gradeButton: {
    paddingHorizontal: spacing.md,
  },
  selectedGrade: {
    backgroundColor: colors.primary,
  },
  startButton: {
    marginTop: spacing.lg,
  },
  gameplayContainer: {
    padding: spacing.md,
  },
  headerContainer: {
    marginBottom: spacing.lg,
  },
  wordContainer: {
    alignItems: 'center',
    marginVertical: spacing.xl,
  },
  speakerButton: {
    padding: spacing.md,
  },
  recognitionContainer: {
    alignItems: 'center',
    marginTop: spacing.lg,
  },
  recognizedText: {
    marginTop: spacing.sm,
    color: colors.text.secondary,
  },
  scoreContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginBottom: 30,
  },
  playerScore: {
    alignItems: 'center',
  },
  winnerText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: 30,
  },
  playAgainButton: {
    backgroundColor: '#4CAF50',
    paddingHorizontal: 30,
  },
  settingHint: {
    marginTop: spacing.xs,
    fontStyle: 'italic',
    textAlign: 'center',
    width: '100%',
  },
  transitionContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.md,
  },
  transitionCard: {
    alignItems: 'center',
    padding: spacing.xl,
  },
  transitionText: {
    textAlign: 'center',
    marginVertical: spacing.lg,
  },
  startTurnButton: {
    minWidth: 200,
    marginTop: spacing.lg,
  },
}); 